import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Input, InputNumber, Select, Space, Table, Tag } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import { API } from 'service/api-route';
import AxiosUtils from 'utils/axios-utils';
import { CreatorLookup, CreatorRule, CreatorRuleMode } from '../types';
import { parseUids } from '../utils';
import styles from '../style.module.scss';

interface Props {
  value: CreatorRule[];
  onChange: (next: CreatorRule[]) => void;
}

const MODE_OPTIONS = [
  { value: 'DIRECT_PAY', label: 'Kiểu 1 · Công ty trả thẳng Creator' },
  { value: 'SHARE', label: 'Kiểu 2 · Chia phí với Agency' },
];

/** Tra TikTok ID của các UID (giữ kết quả đã tra, chỉ hỏi máy chủ UID mới). */
function useLookup(uids: string[]) {
  const [info, setInfo] = useState<Record<string, CreatorLookup>>({});
  const asked = useRef(new Set<string>());
  const key = uids.join(',');
  useEffect(() => {
    const missing = uids.filter((u) => !asked.current.has(u));
    if (!missing.length) return;
    missing.forEach((u) => asked.current.add(u));
    for (let i = 0; i < missing.length; i += 200) {
      const part = missing.slice(i, i + 200);
      AxiosUtils.get(API.agencyFeeCreators, { uids: part.join(',') })
        .then((res: any) => {
          const list: CreatorLookup[] = res?.data ?? [];
          setInfo((prev) => ({ ...prev, ...Object.fromEntries(list.map((x) => [x.uid, x])) }));
        })
        .catch(() => part.forEach((u) => asked.current.delete(u)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return info;
}

const Who = ({ x }: { x?: CreatorLookup }) => {
  if (!x) return <span className={styles.muted}>Đang tra…</span>;
  if (!x.found) return <Tag color="red">Không thấy UID trong dữ liệu TikTok</Tag>;
  return (
    <div className={styles.creator}>
      <strong>@{x.tiktokUsername || '—'}</strong>
      <span className={styles.sub}>
        {x.groupName || 'Không có Nhóm'}
        {x.lastPeriodEnd ? ` · file tới ${x.lastPeriodEnd.slice(8)}/${x.lastPeriodEnd.slice(5, 7)}/${x.lastPeriodEnd.slice(0, 4)}` : ''}
      </span>
    </div>
  );
};

/**
 * Creator đặc biệt theo UID: cả hai kiểu công ty trả thẳng thưởng Online cho Creator.
 * Kiểu 1: Agency nhận đủ phí. Kiểu 2: Agency nhận X% phí nhiệm vụ, công ty giữ phần còn lại.
 */
const CreatorRulesEditor = ({ value, onChange }: Props) => {
  const [text, setText] = useState('');
  const [debounced, setDebounced] = useState('');
  const [mode, setMode] = useState<CreatorRuleMode>('SHARE');
  const [pct, setPct] = useState<number>(50);
  const [q, setQ] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(text), 400);
    return () => clearTimeout(t);
  }, [text]);

  const typed = useMemo(() => parseUids(debounced), [debounced]);
  const existing = useMemo(() => new Set(value.map((r) => r.uid)), [value]);
  const info = useLookup(useMemo(() => Array.from(new Set([...value.map((r) => r.uid), ...typed])), [value, typed]));

  const add = () => {
    const fresh = parseUids(text).filter((u) => !existing.has(u));
    if (!fresh.length) return;
    onChange([...value, ...fresh.map((uid) => ({ uid, mode, agencyPct: mode === 'SHARE' ? pct : 100, note: '' }))]);
    setText('');
    setDebounced('');
  };
  const setRow = (uid: string, patch: Partial<CreatorRule>) => onChange(value.map((r) => (r.uid === uid ? { ...r, ...patch } : r)));

  const needle = q.trim().toLowerCase().replace(/^@/, '');
  const rows = needle
    ? value.filter((r) => r.uid.includes(needle) || (info[r.uid]?.tiktokUsername || '').toLowerCase().includes(needle))
    : value;

  return (
    <Card size="small" title="Creator đặc biệt (theo UID)" className={styles.block}>
      <div className={styles.blockHint}>
        Cả hai kiểu: <strong>công ty trả thẳng thưởng Online cho Creator</strong>, Agency không phải chuyển lại tiền cho Creator này.
        Kiểu 1 — Agency vẫn nhận đủ 100% phí nhiệm vụ của Creator. Kiểu 2 — Agency chỉ nhận X% phí nhiệm vụ (mặc định 50%), công ty giữ phần còn lại.
      </div>
      <div className={styles.inline} style={{ marginBottom: 8, alignItems: 'flex-start' }}>
        <Input.TextArea
          value={text}
          onChange={(e) => setText(e.target.value)}
          autoSize={{ minRows: 1, maxRows: 4 }}
          placeholder="Dán UID (cột B file TikTok) — nhiều UID cách nhau bằng dấu phẩy hoặc xuống dòng"
          style={{ width: 'min(420px, 100%)' }}
        />
        <Select value={mode} options={MODE_OPTIONS} onChange={(v) => setMode(v)} style={{ width: 260 }} />
        {mode === 'SHARE' && (
          <Space size={4}>
            <span>Agency nhận</span>
            <InputNumber min={0} max={100} step={5} value={pct} onChange={(v) => setPct(v === null ? 50 : Number(v))} style={{ width: 80 }} />
            <span>%</span>
          </Space>
        )}
        <Button type="primary" ghost icon={<PlusOutlined />} disabled={!parseUids(text).some((u) => !existing.has(u))} onClick={add}>
          Thêm
        </Button>
      </div>
      {typed.length > 0 && (
        <div className={styles.lookupList}>
          {typed.map((uid) => (
            <div key={uid} className={styles.lookupItem}>
              <span className={styles.money}>{uid}</span>
              <span>→</span>
              <Who x={info[uid]} />
              {existing.has(uid) && <Tag color="gold">Đã có trong danh sách</Tag>}
            </div>
          ))}
        </div>
      )}
      {value.some((r) => info[r.uid] && !info[r.uid].found) && (
        <Alert className={styles.section} type="warning" showIcon message="Có UID chưa thấy trong dữ liệu TikTok đã nhập — kiểm tra lại số UID." />
      )}
      {value.length > 8 && (
        <Input.Search allowClear placeholder="Tìm UID / @TikTok ID trong danh sách" value={q} onChange={(e) => setQ(e.target.value)} style={{ width: 300, marginBottom: 8 }} />
      )}
      <Table
        rowKey="uid"
        size="small"
        dataSource={rows}
        pagination={rows.length > 20 ? { pageSize: 20 } : false}
        locale={{ emptyText: 'Chưa có Creator đặc biệt — mọi Creator tính như thường (Agency nhận phí + nhận thưởng Creator để chuyển lại).' }}
        columns={[
          { title: 'UID', dataIndex: 'uid', width: 190, render: (v) => <span className={styles.money}>{v}</span> },
          { title: 'TikTok ID', key: 'who', render: (_, r) => <Who x={info[r.uid]} /> },
          {
            title: 'Kiểu', key: 'mode', width: 270,
            render: (_, r) => (
              <Select
                value={r.mode}
                options={MODE_OPTIONS}
                onChange={(v: CreatorRuleMode) => setRow(r.uid, { mode: v, agencyPct: v === 'DIRECT_PAY' ? 100 : r.agencyPct === 100 ? 50 : r.agencyPct })}
                style={{ width: '100%' }}
              />
            ),
          },
          {
            title: 'Agency nhận', key: 'pct', width: 130,
            render: (_, r) =>
              r.mode === 'SHARE' ? (
                <Space size={4}>
                  <InputNumber min={0} max={100} step={5} value={r.agencyPct} onChange={(v) => setRow(r.uid, { agencyPct: v === null ? 0 : Number(v) })} style={{ width: 76 }} />
                  <span>%</span>
                </Space>
              ) : (
                <span>100%</span>
              ),
          },
          {
            title: 'Ghi chú', key: 'note', width: 220,
            render: (_, r) => <Input value={r.note} maxLength={200} placeholder="Lý do / thoả thuận" onChange={(e) => setRow(r.uid, { note: e.target.value })} />,
          },
          {
            title: '', key: 'del', width: 48,
            render: (_, r) => <Button size="small" icon={<DeleteOutlined />} onClick={() => onChange(value.filter((x) => x.uid !== r.uid))} />,
          },
        ]}
      />
    </Card>
  );
};

export default CreatorRulesEditor;
